import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  OnDestroy,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';

import { ReturnsService } from '../../services/returns.service';
import { ReturnDetail } from '../../models/return-detail.model';
import { ReturnPosition } from '../../models/return-position.model';
import { ReturnStatus } from '../../models/return-status.enum';
import { PositionStatus } from '../../models/position-status.enum';
import { KatalogArtikel } from '../../models/katalog-artikel.model';

type PositionStatusFilter =
  | 'ALLE'
  | 'OFFEN'
  | 'ERFASST';

type ReasonFilter =
  | 'ALLE'
  | 'OHNE'
  | string;

type SortColumn =
  | 'artikel'
  | 'grund'
  | 'menge'
  | 'gesamtwert';

type SortDirection =
  | 'asc'
  | 'desc';

interface SortState {
  readonly column:
  | SortColumn
  | null;

  readonly direction:
  SortDirection;
}

interface StatusFilterOption {
  readonly label: string;
  readonly value: PositionStatusFilter;
}

interface ReasonFilterOption {
  readonly label: string;
  readonly value: ReasonFilter;
}

interface UndoUpdateEntry {
  readonly kind: 'update';
  readonly positionId: string;
  readonly menge: number;
  readonly gesamtwert: number;
  readonly status: PositionStatus;
  readonly grund: string;
  readonly einheit: string;
  readonly mengeEinheitId: number;
  readonly einzelpreis: number;
  readonly istNeu: boolean;
  readonly previousNewlyAddedPositionId: string | null;
}

interface UndoCreateEntry {
  readonly kind: 'create';
  readonly positionId: string;
  readonly previousNewlyAddedPositionId: string | null;
}

type UndoEntry =
  | UndoUpdateEntry
  | UndoCreateEntry;

const DEFAULT_EINHEIT =
  'Stück';

const NUMPAD_BACKSPACE =
  '⌫';

const NUMPAD_KEYS:
  readonly (
    string |
    null
  )[] = [
    '7',
    '8',
    '9',
    '4',
    '5',
    '6',
    '1',
    '2',
    '3',
    null,
    '0',
    NUMPAD_BACKSPACE,
  ];

const SUMMARY_SCROLL_TOLERANCE =
  4;

@Component({
  selector:
    'app-returns-detail',

  standalone:
    true,

  imports: [
    CommonModule,
    MatIconModule,
  ],

  templateUrl:
    './returns-detail.component.html',

  styleUrl:
    './returns-detail.component.scss',

  changeDetection:
    ChangeDetectionStrategy.OnPush,
})
export class ReturnsDetailComponent implements OnDestroy {
  private readonly route =
    inject(
      ActivatedRoute
    );

  private readonly router =
    inject(
      Router
    );

  private readonly returnsService =
    inject(
      ReturnsService
    );

  private lockedFilialNummer: number | null = null;
  private readonly pendingSaveRowIds = new Set<string>();
  private saveInFlight = false;

  readonly isSavingPositions = signal(false);

  readonly detail =
    signal<
      ReturnDetail |
      null
    >(
      null
    );

  readonly isLoading =
    signal(
      true
    );

  private readonly positionenState =
    signal<
      ReturnPosition[]
    >(
      []
    );

  readonly selection =
    signal<
      ReadonlySet<string>
    >(
      new Set<string>()
    );

  readonly numpadKeys =
    NUMPAD_KEYS;

  readonly numpadBackspace =
    NUMPAD_BACKSPACE;

  readonly activeId =
    signal<
      string |
      null
    >(
      null
    );

  readonly activeUnit =
    signal<string>(
      DEFAULT_EINHEIT
    );

  readonly inputValue =
    signal(
      ''
    );

  readonly placeholderValue =
    signal(
      ''
    );

  readonly flashId =
    signal<
      string |
      null
    >(
      null
    );

  readonly newlyAddedPositionId =
    signal<
      string |
      null
    >(
      null
    );

  private readonly undoStack =
    signal<
      readonly UndoEntry[]
    >(
      []
    );

  readonly searchTerm =
    signal(
      ''
    );

  readonly statusFilter =
    signal<
      PositionStatusFilter
    >(
      'ALLE'
    );

  readonly reasonFilter =
    signal<
      ReasonFilter
    >(
      'ALLE'
    );

  readonly isCompact =
    signal(
      false
    );

  readonly sortState =
    signal<SortState>({
      column:
        null,

      direction:
        'asc',
    });

  readonly skeletonKpiCards =
    Array.from({
      length:
        3,
    });

  readonly skeletonRows =
    Array.from({
      length:
        6,
    });

  readonly statusFilters:
    readonly StatusFilterOption[] = [
      {
        label:
          'Alle',

        value:
          'ALLE',
      },
      {
        label:
          'Offen',

        value:
          'OFFEN',
      },
      {
        label:
          'Erfasst',

        value:
          'ERFASST',
      },
    ];

  readonly gruende =
    signal<
      readonly string[]
    >(
      []
    );

  readonly einheiten =
    signal<
      readonly string[]
    >(
      []
    );

  readonly reasonFilters =
    computed<
      readonly ReasonFilterOption[]
    >(
      () => [
        {
          label:
            'Grund: Alle',

          value:
            'ALLE',
        },
        {
          label:
            'Ohne Grund',

          value:
            'OHNE',
        },
        ...this.gruende().map(
          (
            grund
          ) => ({
            label:
              grund,

            value:
              grund,
          })
        ),
      ]
    );

  private readonly positionen =
    computed<
      ReturnPosition[]
    >(
      () =>
        this.positionenState()
    );

  readonly displayedPositionen =
    computed<
      ReturnPosition[]
    >(
      () => {
        const term =
          this.searchTerm()
            .trim()
            .toLowerCase();

        const status =
          this.statusFilter();

        const reason =
          this.reasonFilter();

        const filtered =
          this.positionen().filter(
            (
              position
            ) => {
              if (
                term
              ) {
                const matchesArtikel =
                  position.artikelName
                    .toLowerCase()
                    .includes(
                      term
                    );

                const matchesGrund =
                  position.grund
                    .toLowerCase()
                    .includes(
                      term
                    );

                if (
                  !matchesArtikel &&
                  !matchesGrund
                ) {
                  return false;
                }
              }

              if (
                status ===
                'OFFEN' &&
                !this.isOffen(
                  position
                )
              ) {
                return false;
              }

              if (
                status ===
                'ERFASST' &&
                position.status !==
                PositionStatus.Erfasst
              ) {
                return false;
              }

              if (
                reason ===
                'OHNE' &&
                position.grund !==
                ''
              ) {
                return false;
              }

              if (
                reason !==
                'ALLE' &&
                reason !==
                'OHNE' &&
                position.grund !==
                reason
              ) {
                return false;
              }

              return true;
            }
          );

        return this.sortPositionen(
          filtered
        );
      }
    );

  readonly offenePositionen =
    computed(
      () =>
        this.positionen().filter(
          (
            position
          ) =>
            this.isOffen(
              position
            )
        ).length
    );

  readonly gesamtwert =
    computed(
      () =>
        this.positionen().reduce(
          (
            sum,
            position
          ) =>
            sum +
            position.gesamtwert,
          0
        )
    );

  readonly gesamtCount =
    computed(
      () =>
        this.positionen()
          .length
    );

  readonly gefiltertCount =
    computed(
      () =>
        this.displayedPositionen()
          .length
    );

  readonly selectionCount =
    computed(
      () =>
        this.selection()
          .size
    );

  readonly selectablePositionen =
    computed<
      ReturnPosition[]
    >(
      () =>
        this.displayedPositionen().filter(
          (
            position
          ) =>
            this.isOffen(
              position
            )
        )
    );

  readonly allFilteredSelected =
    computed(
      () => {
        const selectable =
          this.selectablePositionen();

        const selected =
          this.selection();

        return (
          selectable.length >
          0 &&
          selectable.every(
            (
              position
            ) =>
              selected.has(
                position.rowId
              )
          )
        );
      }
    );

  readonly someFilteredSelected =
    computed(
      () => {
        const selectable =
          this.selectablePositionen();

        const selected =
          this.selection();

        const selectedCount =
          selectable.filter(
            (
              position
            ) =>
              selected.has(
                position.rowId
              )
          ).length;

        return (
          selectedCount >
          0 &&
          selectedCount <
          selectable.length
        );
      }
    );

  readonly erfassteCount =
    computed(
      () =>
        this.positionen().filter(
          (
            position
          ) =>
            position.status ===
            PositionStatus.Erfasst
        ).length
    );

  readonly activePosition =
    computed<
      ReturnPosition |
      null
    >(
      () => {
        const id =
          this.activeId();

        if (
          id ===
          null
        ) {
          return null;
        }

        return (
          this.positionen().find(
            (
              position
            ) =>
              position.rowId ===
              id
          ) ??
          null
        );
      }
    );

  readonly activeUnitOption =
    computed(
      () => {
        const position =
          this.activePosition();

        if (
          position ===
          null
        ) {
          return null;
        }

        return this.findUnitOption(
          position,
          this.activeUnit()
        );
      }
    );

  readonly liveGesamtwert =
    computed<
      number |
      null
    >(
      () => {
        const option =
          this.activeUnitOption();

        const parsed =
          this.parseMenge(
            this.inputValue()
          );

        if (
          option ===
          null ||
          parsed ===
          null
        ) {
          return null;
        }

        return this.roundCurrency(
          parsed *
          option.einzelpreis
        );
      }
    );

  readonly canUndo =
    computed(
      () =>
        this.undoStack()
          .length >
        0
    );

  readonly isAddOpen =
    signal(
      false
    );

  readonly addQuery =
    signal(
      ''
    );

  readonly katalogTreffer =
    computed(
      () => {
        const vorhandeneArtikelnummern = new Set(
          this.positionen().map((position) => position.artikelnummer)
        );

        return this.returnsService
          .searchKatalog(this.addQuery())
          .filter(
            (artikel) =>
              !vorhandeneArtikelnummern.has(artikel.artikelnummer)
          );
      }
    );

  readonly isSummaryOpen =
    signal(
      false
    );

  readonly summaryReviewed =
    signal(
      false
    );

  readonly isClosing =
    signal(
      false
    );

  readonly closeError =
    signal(
      false
    );

  readonly isReadOnly =
    computed(
      () =>
        this.detail()?.status ===
        ReturnStatus.Erfasst
    );

  readonly canClose =
    computed(
      () =>
        !this.isReadOnly() &&
        !this.isSavingPositions() &&
        this.erfassteCount() > 0
    );

  readonly summaryPositionen =
    computed<
      ReturnPosition[]
    >(
      () =>
        this.positionen()
    );

  private readonly summaryList =
    viewChild<
      ElementRef<HTMLElement>
    >(
      'summaryList'
    );

  private readonly tableBody =
    viewChild<
      ElementRef<HTMLElement>
    >(
      'tableBody'
    );

  private readonly numpadPanel =
    viewChild<
      ElementRef<HTMLElement>
    >(
      'numpadPanel'
    );

  constructor() {
    this.loadLookups();

    this.route.paramMap.subscribe(
      (
        params
      ) => {
        const raw =
          params.get(
            'filialNummer'
          );

        const filialNummer =
          Number(
            raw
          );

        if (
          !raw ||
          !Number.isInteger(
            filialNummer
          ) ||
          filialNummer <=
          0
        ) {
          void this.router.navigate(
            [
              '/retouren',
            ]
          );

          return;
        }

        this.openDetailWithLock(
          filialNummer
        );
      }
    );

    effect(
      (
        onCleanup
      ) => {
        const element =
          this.summaryList()
            ?.nativeElement;

        if (
          !element
        ) {
          return;
        }

        this.summaryPositionen();

        untracked(
          () =>
            this.syncSummaryReviewed(
              element
            )
        );

        const observer =
          new ResizeObserver(
            () =>
              this.syncSummaryReviewed(
                element
              )
          );

        observer.observe(
          element
        );

        const content =
          element.firstElementChild;

        if (
          content
        ) {
          observer.observe(
            content
          );
        }

        onCleanup(
          () =>
            observer.disconnect()
        );
      }
    );
  }

  goBack(): void {
    this.releaseLockAndNavigate();
  }

  ngOnDestroy(): void {
    const filialNummer = this.lockedFilialNummer;

    if (filialNummer === null) {
      return;
    }

    this.lockedFilialNummer = null;
    this.returnsService.releaseReturnLock(filialNummer).subscribe({
      error: () => this.returnsService.clearReturnLock(filialNummer),
    });
  }

  @HostListener('window:beforeunload')
  releaseLockBeforeUnload(): void {
    const filialNummer = this.lockedFilialNummer;

    if (filialNummer === null) {
      return;
    }

    this.lockedFilialNummer = null;
    this.returnsService.releaseReturnLockBeforeUnload(filialNummer);
  }

  setSort(
    column:
      SortColumn
  ): void {
    const currentSort =
      this.sortState();

    const firstDirection:
      SortDirection =
      column ===
        'menge' ||
        column ===
        'gesamtwert'
        ? 'desc'
        : 'asc';

    const secondDirection:
      SortDirection =
      firstDirection ===
        'asc'
        ? 'desc'
        : 'asc';

    if (
      currentSort.column !==
      column
    ) {
      this.sortState.set({
        column,
        direction:
          firstDirection,
      });

      return;
    }

    if (
      currentSort.direction ===
      firstDirection
    ) {
      this.sortState.set({
        column,
        direction:
          secondDirection,
      });

      return;
    }

    this.sortState.set({
      column:
        null,

      direction:
        'asc',
    });
  }

  isSortedBy(
    column:
      SortColumn
  ): boolean {
    return (
      this.sortState()
        .column ===
      column
    );
  }

  getSortIcon(
    column:
      SortColumn
  ): string {
    if (
      !this.isSortedBy(
        column
      )
    ) {
      return 'unfold_more';
    }

    return (
      this.sortState()
        .direction ===
        'asc'
        ? 'keyboard_arrow_up'
        : 'keyboard_arrow_down'
    );
  }

  private sortPositionen(
    positionen:
      ReturnPosition[]
  ): ReturnPosition[] {
    const {
      column,
      direction,
    } =
      this.sortState();

    if (
      column ===
      null
    ) {
      // The backend's natural/default Retouren order is Artikelnummer ASC.
      // Keep that order explicit on the client as well so newly added rows
      // are inserted at their real table position instead of staying at the
      // end of the local array until the next reload.
      return [
        ...positionen,
      ].sort(
        (first, second) =>
          first.artikelnummer -
          second.artikelnummer
      );
    }

    const factor =
      direction ===
        'asc'
        ? 1
        : -1;

    return [
      ...positionen,
    ].sort(
      (
        first,
        second
      ) => {
        switch (
        column
        ) {
          case 'artikel':
            return (
              first.artikelName.localeCompare(
                second.artikelName,
                'de',
                {
                  sensitivity:
                    'base',

                  numeric:
                    true,
                }
              ) *
              factor
            );

          case 'grund':
            return (
              first.grund.localeCompare(
                second.grund,
                'de',
                {
                  sensitivity:
                    'base',

                  numeric:
                    true,
                }
              ) *
              factor
            );

          case 'menge':
            return (
              (
                first.menge -
                second.menge
              ) *
              factor
            );

          case 'gesamtwert':
            return (
              (
                first.gesamtwert -
                second.gesamtwert
              ) *
              factor
            );

          default:
            return 0;
        }
      }
    );
  }

  openSummary(): void {
    if (
      !this.canClose()
    ) {
      return;
    }

    this.summaryReviewed.set(
      false
    );

    this.closeError.set(
      false
    );

    this.isSummaryOpen.set(
      true
    );
  }

  closeSummary(): void {
    if (
      this.isClosing()
    ) {
      return;
    }

    this.isSummaryOpen.set(
      false
    );
  }

  onSummaryScroll(
    element:
      HTMLElement
  ): void {
    this.syncSummaryReviewed(
      element
    );
  }

  private syncSummaryReviewed(
    element:
      HTMLElement
  ): void {
    if (
      this.summaryReviewed()
    ) {
      return;
    }

    const remaining =
      element.scrollHeight -
      element.clientHeight -
      element.scrollTop;

    if (
      remaining <=
      SUMMARY_SCROLL_TOLERANCE
    ) {
      this.summaryReviewed.set(
        true
      );
    }
  }

  confirmClose(): void {
    const filialNummer =
      this.detail()
        ?.filialNummer;

    if (
      filialNummer ===
      undefined ||
      !this.canClose() ||
      !this.summaryReviewed() ||
      this.isClosing()
    ) {
      return;
    }

    this.isClosing.set(
      true
    );

    this.closeError.set(
      false
    );

    this.returnsService
      .heartbeatReturnLock(filialNummer)
      .pipe(
        switchMap(() =>
          this.returnsService.closeReturn(
            filialNummer,
            this.offenePositionen() > 0
          )
        ),
        finalize(
          () =>
            this.isClosing.set(
              false
            )
        )
      )
      .subscribe({
        next: () => {
          this.isSummaryOpen.set(
            false
          );

          this.lockedFilialNummer = null;
          this.returnsService.clearReturnLock(filialNummer);
          void this.router.navigate(['/retouren']);
        },

        error: (error: unknown) => {
          if (this.handleMutationLockError(filialNummer, error)) {
            return;
          }

          this.closeError.set(
            true
          );
        },
      });
  }

  setSearchTerm(
    value:
      string
  ): void {
    this.searchTerm.set(
      value
    );
  }

  setStatusFilter(
    value:
      PositionStatusFilter
  ): void {
    this.statusFilter.set(
      value
    );
  }

  setReasonFilter(
    value:
      ReasonFilter
  ): void {
    this.reasonFilter.set(
      value
    );
  }

  toggleCompact(): void {
    this.isCompact.update(
      (
        current
      ) =>
        !current
    );
  }

  changeGrund(
    position:
      ReturnPosition,
    grund:
      string
  ): void {
    if (
      grund ===
      position.grund
    ) {
      return;
    }

    this.pushUndo(
      position
    );

    this.positionenState.update(
      (
        positionen
      ) =>
        positionen.map(
          (
            current
          ) =>
            current.rowId ===
              position.rowId
              ? {
                ...current,
                grund,
              }
              : current
        )
    );
  }

  private readonly einheitBesitzer =
    computed(
      () => {
        const index =
          new Map<
            number,
            Map<
              string,
              string
            >
          >();

        for (
          const position of
          this.positionen()
        ) {
          let einheiten =
            index.get(
              position.artikelnummer
            );

          if (
            !einheiten
          ) {
            einheiten =
              new Map<
                string,
                string
              >();

            index.set(
              position.artikelnummer,
              einheiten
            );
          }

          einheiten.set(
            position.einheit,
            position.rowId
          );
        }

        return index;
      }
    );

  istEinheitBelegt(
    position:
      ReturnPosition,
    einheit:
      string
  ): boolean {
    const besitzer =
      this.einheitBesitzer()
        .get(
          position.artikelnummer
        )
        ?.get(
          einheit
        );

    return (
      besitzer !==
      undefined &&
      besitzer !==
      position.rowId
    );
  }

  changeEinheit(
    position:
      ReturnPosition,
    einheit:
      string
  ): void {
    if (this.isReadOnly()) {
      return;
    }

    const option =
      this.findUnitOption(
        position,
        einheit
      );

    if (
      option ===
      null ||
      option.einheit ===
      position.einheit ||
      this.istEinheitBelegt(
        position,
        option.einheit
      )
    ) {
      return;
    }

    this.pushUndo(
      position
    );

    this.positionenState.update(
      (
        positionen
      ) =>
        positionen.map(
          (
            current
          ) =>
            current.rowId ===
              position.rowId
              ? {
                ...current,

                einheit:
                  option.einheit,

                mengeEinheitId:
                  option.id ?? this.returnsService.unitIdFromLabel(option.einheit) ?? current.mengeEinheitId,

                einzelpreis:
                  option.einzelpreis,

                gesamtwert:
                  this.isErfasst(
                    current
                  )
                    ? this.roundCurrency(
                      current.menge *
                      option.einzelpreis
                    )
                    : 0,
              }
              : current
        )
    );

    // Artikel + Einheit is one backend row. Persist only the row that changed.
    if (this.isErfasst(position)) {
      this.persistPosition(position.rowId);
    }

    if (
      this.activeId() ===
      position.rowId
    ) {
      this.activeUnit.set(
        option.einheit
      );

      this.inputValue.set(
        ''
      );

      this.placeholderValue.set(
        this.mengeRohwert(
          position
        )
      );
    }
  }

  markErfasst(
    position:
      ReturnPosition
  ): void {
    this.openNumpad(
      position
    );
  }

  isSelected(
    position:
      ReturnPosition
  ): boolean {
    return this.selection().has(
      position.rowId
    );
  }

  isSelectable(
    position:
      ReturnPosition
  ): boolean {
    return this.isOffen(
      position
    );
  }

  toggleSelect(
    position:
      ReturnPosition
  ): void {
    if (
      !this.isSelectable(
        position
      )
    ) {
      return;
    }

    this.selection.update(
      (
        current
      ) => {
        const next =
          new Set(
            current
          );

        if (
          next.has(
            position.rowId
          )
        ) {
          next.delete(
            position.rowId
          );
        } else {
          next.add(
            position.rowId
          );
        }

        return next;
      }
    );
  }

  toggleSelectAllFiltered(): void {
    const selectable =
      this.selectablePositionen();

    const allSelected =
      this.allFilteredSelected();

    this.selection.update(
      (
        current
      ) => {
        const next =
          new Set(
            current
          );

        for (
          const position of
          selectable
        ) {
          if (
            allSelected
          ) {
            next.delete(
              position.rowId
            );
          } else {
            next.add(
              position.rowId
            );
          }
        }

        return next;
      }
    );
  }

  clearSelection(): void {
    this.selection.set(
      new Set<string>()
    );
  }

  bulkMarkErfasst(): void {
    if (this.isReadOnly()) {
      return;
    }

    const filialNummer =
      this.detail()?.filialNummer;

    if (
      filialNummer === undefined
    ) {
      return;
    }

    const selectedIds =
      this.selection();

    const selectedPositions =
      this.positionen().filter(
        (position) =>
          selectedIds.has(position.rowId) &&
          this.isOffen(position)
      );

    if (
      selectedPositions.length === 0
    ) {
      return;
    }

    for (const position of selectedPositions) {
      this.pushUndo(position);
    }

    this.positionenState.update(
      (positionen) =>
        positionen.map(
          (position) =>
            selectedIds.has(position.rowId) &&
            this.isOffen(position)
              ? {
                  ...position,
                  status: PositionStatus.Erfasst,
                  istNeu: false,
                }
              : position
        )
    );

    const highlightedId =
      this.newlyAddedPositionId();

    if (
      highlightedId !== null &&
      selectedIds.has(highlightedId)
    ) {
      this.newlyAddedPositionId.set(null);
    }

    this.clearSelection();

    this.persistPositions(
      selectedPositions.map((position) => position.rowId)
    );
  }

  openNumpad(
    position:
      ReturnPosition
  ): void {
    if (this.isReadOnly()) {
      return;
    }

    this.focusRow(
      position.rowId,
      this.mengeRohwert(
        position
      ),
      position.einheit
    );
  }

  getMengeText(
    position:
      ReturnPosition
  ): string {
    return this.isErfasst(
      position
    )
      ? String(
        position.menge
      )
      : '—';
  }

  private mengeRohwert(
    position:
      ReturnPosition
  ): string {
    return this.isErfasst(
      position
    )
      ? String(
        position.menge
      )
      : '';
  }

  closeNumpad(): void {
    this.focusRow(
      null,
      ''
    );
  }

  isNumpadOpenFor(
    position:
      ReturnPosition
  ): boolean {
    return (
      this.activeId() ===
      position.rowId
    );
  }

  isFlashing(
    position:
      ReturnPosition
  ): boolean {
    return (
      this.flashId() ===
      position.rowId
    );
  }

  getRowClass(
    position:
      ReturnPosition,
    index:
      number
  ): string {
    if (
      this.isFlashing(
        position
      )
    ) {
      return 'row-flash';
    }

    if (
      this.isNumpadOpenFor(
        position
      )
    ) {
      return 'row-active';
    }

    if (
      position.status ===
      PositionStatus.Erfasst
    ) {
      return this.zebra(
        index
      );
    }

    if (
      this.isOffen(
        position
      )
    ) {
      return 'row-offen';
    }

    return this.zebra(
      index
    );
  }

  private zebra(
    index:
      number
  ): string {
    return (
      index %
        2 ===
        0
        ? 'row-even'
        : 'row-odd'
    );
  }

  selectUnit(
    einheit:
      string
  ): void {
    const active =
      this.activePosition();

    if (
      active ===
      null ||
      this.findUnitOption(
        active,
        einheit
      ) ===
      null
    ) {
      return;
    }

    if (
      einheit ===
      this.activeUnit()
    ) {
      return;
    }

    const draft =
      this.inputValue()
        .trim();

    if (
      draft !==
      ''
    ) {
      const menge =
        this.parseMenge(
          draft
        );

      if (
        menge ===
        null
      ) {
        return;
      }

      const touched =
        this.commitMenge(
          active.rowId,
          menge,
          this.activeUnit()
        );

      if (
        touched !==
        null
      ) {
        this.flashRow(
          touched
        );
      }
    }

    const targetRow =
      this.positionen().find(
        (
          position
        ) =>
          position.artikelnummer ===
          active.artikelnummer &&
          position.einheit ===
          einheit
      );

    this.activeUnit.set(
      einheit
    );

    if (
      targetRow
    ) {
      this.activeId.set(
        targetRow.rowId
      );
    }

    this.inputValue.set(
      ''
    );

    this.placeholderValue.set(
      targetRow &&
        this.isErfasst(
          targetRow
        )
        ? String(
          targetRow.menge
        )
        : ''
    );
  }

  isUnitErfasst(
    einheit:
      string
  ): boolean {
    return (
      this.unitRow(
        einheit
      )
        ?.status ===
      PositionStatus.Erfasst
    );
  }

  unitMenge(
    einheit:
      string
  ): number | null {
    return (
      this.unitRow(
        einheit
      )
        ?.menge ??
      null
    );
  }

  private unitRow(
    einheit:
      string
  ):
    | ReturnPosition
    | undefined {
    const active =
      this.activePosition();

    if (
      active ===
      null
    ) {
      return undefined;
    }

    return this.positionen().find(
      (
        current
      ) =>
        current.artikelnummer ===
        active.artikelnummer &&
        current.einheit ===
        einheit
    );
  }

  openAddDialog(): void {
    if (this.isReadOnly()) {
      return;
    }

    this.addQuery.set(
      ''
    );

    this.isAddOpen.set(
      true
    );

    const filialNummer =
      this.detail()?.filialNummer;

    if (
      filialNummer === undefined
    ) {
      return;
    }

    // Load the catalog only when the user actually opens "Artikel hinzufügen".
    // Capturing/saving a position must not trigger this endpoint.
    this.returnsService
      .loadAvailableArticles(
        filialNummer
      )
      .subscribe({ error: () => undefined });
  }

  closeAddDialog(): void {
    this.isAddOpen.set(
      false
    );
  }

  setAddQuery(
    value:
      string
  ): void {
    this.addQuery.set(
      value
    );
  }

  addKatalogArtikel(
    artikel:
      KatalogArtikel
  ): void {
    if (this.isReadOnly()) {
      return;
    }

    // An article can only be added once from the modal. Further units for the
    // same article are created from the table/numpad flow.
    if (
      this.positionen().some(
        (position) => position.artikelnummer === artikel.artikelnummer
      )
    ) {
      this.closeAddDialog();
      return;
    }

    const previousNewlyAddedPositionId =
      this.newlyAddedPositionId();

    const initialUnit =
      artikel.verfuegbareEinheiten.find(
        (option) => option.id === artikel.grundEinheitId
      ) ?? artikel.verfuegbareEinheiten[0];

    if (!initialUnit) {
      return;
    }

    const neuePosition:
      ReturnPosition = {
      rowId:
        this.createRowId(
          artikel.artikelnummer
        ),

      positionId:
        null,

      artikelnummer:
        artikel.artikelnummer,

      artikelName:
        artikel.artikelName,

      grund:
        '',

      menge:
        0,

      einheit:
        initialUnit.einheit,

      mengeEinheitId:
        initialUnit.id ?? artikel.grundEinheitId,

      grundEinheitId:
        artikel.grundEinheitId,

      einzelpreis:
        initialUnit.einzelpreis,

      gesamtwert:
        0,

      status:
        PositionStatus.Gemeldet,

      istNeu:
        true,

      verfuegbareEinheiten:
        artikel.verfuegbareEinheiten,
    };

    this.pushCreatedUndo(
      neuePosition.rowId,
      previousNewlyAddedPositionId
    );

    // Insert using the table's current sort immediately. This prevents the
    // row from flashing at the bottom before the rendered list applies the
    // active sort, and lets the following auto-scroll target its final place.
    this.positionenState.update(
      (positionen) =>
        this.sortPositionen([
          ...positionen,
          neuePosition,
        ])
    );

    this.newlyAddedPositionId.set(
      neuePosition.rowId
    );

    this.closeAddDialog();

    this.openNumpad(
      neuePosition
    );

    this.scrollPositionIntoView(
      neuePosition.rowId
    );
  }

  istNeuOhneMenge(
    position:
      ReturnPosition
  ): boolean {
    return (
      this.newlyAddedPositionId() ===
      position.rowId &&
      !this.isErfasst(
        position
      )
    );
  }

  private createRowId(
    artikelnummer:
      number
  ): string {
    const taken =
      new Set(
        this.positionen().map(
          (
            position
          ) =>
            position.rowId
        )
      );

    const base =
      String(
        artikelnummer
      );

    if (
      !taken.has(
        base
      )
    ) {
      return base;
    }

    let suffix =
      2;

    while (
      taken.has(
        `${base}#neu${suffix}`
      )
    ) {
      suffix++;
    }

    return `${base}#neu${suffix}`;
  }

  liveMenge(
    position:
      ReturnPosition
  ): number {
    if (
      !this.isNumpadOpenFor(
        position
      ) ||
      this.activeUnit() !==
      position.einheit
    ) {
      return position.menge;
    }

    return (
      this.parseMenge(
        this.inputValue()
      ) ??
      position.menge
    );
  }

  rowGesamtwert(
    position:
      ReturnPosition
  ): number {
    if (
      !this.isNumpadOpenFor(
        position
      ) ||
      this.activeUnit() !==
      position.einheit
    ) {
      return position.gesamtwert;
    }

    return this.roundCurrency(
      this.liveMenge(
        position
      ) *
      position.einzelpreis
    );
  }

  onInputChange(
    element:
      HTMLInputElement
  ): void {
    const sanitized =
      this.sanitizeDigits(
        element.value
      );

    if (
      element.value !==
      sanitized
    ) {
      element.value =
        sanitized;
    }

    this.inputValue.set(
      sanitized
    );
  }

  pressKey(
    key:
      string |
      null
  ): void {
    if (
      key ===
      null
    ) {
      return;
    }

    if (
      key ===
      NUMPAD_BACKSPACE
    ) {
      this.inputValue.update(
        (
          value
        ) =>
          value.slice(
            0,
            -1
          )
      );

      return;
    }

    this.inputValue.update(
      (
        value
      ) =>
        this.sanitizeDigits(
          value +
          key
        )
    );
  }

  stepMenge(
    delta:
      number
  ): void {
    const base =
      this.currentDraftMenge();

    const next =
      Math.max(
        0,
        base +
        delta
      );

    this.inputValue.set(
      String(
        next
      )
    );
  }

  confirmAndNext(): void {
    const id =
      this.activeId();

    if (
      id ===
      null
    ) {
      return;
    }

    const menge =
      this.parseMenge(
        this.inputValue()
      );

    if (
      menge ===
      null
    ) {
      this.focusRow(
        null,
        ''
      );

      return;
    }

    const touched =
      this.commitMenge(
        id,
        menge,
        this.activeUnit()
      );

    if (
      touched !==
      null
    ) {
      this.flashRow(
        touched
      );
    }

    this.gotoNextOpen(
      touched ??
      id
    );
  }

  weitereEinheit(): void {
    const active =
      this.activePosition();

    if (
      active ===
      null ||
      active.verfuegbareEinheiten
        .length ===
      0
    ) {
      return;
    }

    const units =
      active.verfuegbareEinheiten.map(
        (
          option
        ) =>
          option.einheit
      );

    if (
      this.inputValue()
        .trim() !==
      ''
    ) {
      const menge =
        this.parseMenge(
          this.inputValue()
        );

      if (
        menge ===
        null
      ) {
        return;
      }

      const touched =
        this.commitMenge(
          active.rowId,
          menge,
          this.activeUnit()
        );

      if (
        touched !==
        null
      ) {
        this.flashRow(
          touched
        );
      }
    }

    const currentIndex =
      units.indexOf(
        this.activeUnit()
      );

    const nextUnit =
      units[
      (
        currentIndex >=
          0
          ? currentIndex +
          1
          : 0
      ) %
      units.length
      ];

    const nextRow =
      this.positionen().find(
        (
          position
        ) =>
          position.artikelnummer ===
          active.artikelnummer &&
          position.einheit ===
          nextUnit
      );

    this.activeUnit.set(
      nextUnit
    );

    if (
      nextRow
    ) {
      this.activeId.set(
        nextRow.rowId
      );
    }

    this.inputValue.set(
      ''
    );

    this.placeholderValue.set(
      nextRow &&
        this.isErfasst(
          nextRow
        )
        ? String(
          nextRow.menge
        )
        : ''
    );
  }

  undo(): void {
    const stack =
      this.undoStack();

    if (
      stack.length ===
      0
    ) {
      return;
    }

    const last =
      stack[
      stack.length -
      1
      ];

    if (
      last.kind ===
      'create'
    ) {
      this.positionenState.update(
        (
          positionen
        ) =>
          positionen.filter(
            (
              position
            ) =>
              position.rowId !==
              last.positionId
          )
      );

      this.selection.update(
        (
          current
        ) => {
          if (
            !current.has(
              last.positionId
            )
          ) {
            return current;
          }

          const next =
            new Set(
              current
            );

          next.delete(
            last.positionId
          );

          return next;
        }
      );

      if (
        this.activeId() ===
        last.positionId
      ) {
        this.focusRow(
          null,
          ''
        );
      }

      this.newlyAddedPositionId.set(
        this.positionExists(
          last.previousNewlyAddedPositionId
        )
          ? last.previousNewlyAddedPositionId
          : null
      );
    } else {
      this.positionenState.update(
        (
          positionen
        ) =>
          positionen.map(
            (
              current
            ) =>
              current.rowId ===
                last.positionId
                ? {
                  ...current,

                  menge:
                    last.menge,

                  gesamtwert:
                    last.gesamtwert,

                  status:
                    last.status,

                  grund:
                    last.grund,

                  einheit:
                    last.einheit,

                  mengeEinheitId:
                    last.mengeEinheitId,

                  einzelpreis:
                    last.einzelpreis,

                  istNeu:
                    last.istNeu,
                }
                : current
          )
      );

      this.newlyAddedPositionId.set(
        this.positionExists(
          last.previousNewlyAddedPositionId
        )
          ? last.previousNewlyAddedPositionId
          : null
      );

      if (
        this.activeId() ===
        last.positionId
      ) {
        const restored =
          this.positionen().find(
            (
              position
            ) =>
              position.rowId ===
              last.positionId
          );

        if (
          restored
        ) {
          this.activeUnit.set(
            restored.einheit
          );

          this.inputValue.set(
            ''
          );

          this.placeholderValue.set(
            this.mengeRohwert(
              restored
            )
          );
        }
      }
    }

    this.undoStack.set(
      stack.slice(
        0,
        -1
      )
    );
  }

  @HostListener(
    'document:mousedown',
    [
      '$event',
    ]
  )
  onDocumentMousedown(
    event:
      MouseEvent
  ): void {
    if (
      this.activeId() ===
      null
    ) {
      return;
    }

    const target =
      event.target;

    if (
      !(
        target instanceof
        Node
      )
    ) {
      return;
    }

    if (
      this.numpadPanel()
        ?.nativeElement
        .contains(
          target
        )
    ) {
      return;
    }

    const element =
      target instanceof
        Element
        ? target
        : target.parentElement;

    if (
      element?.closest(
        '[data-qty-cell]'
      )
    ) {
      return;
    }

    this.saveDraftAndClose();
  }

  private saveDraftAndClose(): void {
    const id =
      this.activeId();

    if (
      id ===
      null
    ) {
      return;
    }

    const menge =
      this.parseMenge(
        this.inputValue()
      );

    if (
      menge !==
      null
    ) {
      this.commitMenge(
        id,
        menge,
        this.activeUnit()
      );
    }

    this.focusRow(
      null,
      ''
    );
  }

  @HostListener(
    'document:keydown',
    [
      '$event',
    ]
  )
  onKeydown(
    event:
      KeyboardEvent
  ): void {
    if (
      (
        event.ctrlKey ||
        event.metaKey
      ) &&
      event.key
        .toLowerCase() ===
      'z'
    ) {
      event.preventDefault();

      this.undo();

      return;
    }

    const id =
      this.activeId();

    if (
      id ===
      null
    ) {
      return;
    }

    if (
      event.key ===
      'Enter'
    ) {
      event.preventDefault();

      this.confirmAndNext();

      return;
    }

    if (
      event.key ===
      'Escape'
    ) {
      event.preventDefault();

      this.focusRow(
        null,
        ''
      );

      return;
    }

    if (
      event.key ===
      'ArrowDown'
    ) {
      event.preventDefault();

      this.moveActive(
        id,
        1
      );

      return;
    }

    if (
      event.key ===
      'ArrowUp'
    ) {
      event.preventDefault();

      this.moveActive(
        id,
        -1
      );
    }
  }

  isStatusFilterActive(
    value:
      PositionStatusFilter
  ): boolean {
    return (
      this.statusFilter() ===
      value
    );
  }

  getReturnStatusLabel(
    status:
      ReturnStatus
  ): string {
    switch (
    status
    ) {
      case ReturnStatus.Offen:
        return 'Offen';

      case ReturnStatus.InBearbeitung:
        return 'In Bearbeitung';

      case ReturnStatus.Erfasst:
        return 'Erfasst';
    }
  }

  getReturnStatusClass(
    status:
      ReturnStatus
  ): string {
    switch (
    status
    ) {
      case ReturnStatus.Offen:
        return 'status-offen';

      case ReturnStatus.InBearbeitung:
        return 'status-in-bearbeitung';

      case ReturnStatus.Erfasst:
        return 'status-abgeschlossen';
    }
  }

  getPositionStatusLabel(
    status:
      PositionStatus
  ): string {
    switch (
    status
    ) {
      case PositionStatus.Gemeldet:
      case PositionStatus.InPruefung:
        return 'Offen';

      case PositionStatus.Erfasst:
        return 'Erfasst';
    }
  }

  getPositionStatusClass(
    status:
      PositionStatus
  ): string {
    switch (
    status
    ) {
      case PositionStatus.Gemeldet:
      case PositionStatus.InPruefung:
        return 'position-status-offen';

      case PositionStatus.Erfasst:
        return 'position-status-erfasst';
    }
  }

  isRowStatus(
    position:
      ReturnPosition,
    status:
      | 'GEMELDET'
      | 'ERFASST'
  ): boolean {
    return (
      position.status ===
      status
    );
  }

  isOffen(
    position:
      ReturnPosition
  ): boolean {
    return (
      position.status ===
      PositionStatus.Gemeldet ||
      position.status ===
      PositionStatus.InPruefung
    );
  }

  isErfasst(
    position:
      ReturnPosition
  ): boolean {
    return (
      position.status ===
      PositionStatus.Erfasst
    );
  }

  formatCurrency(
    value:
      number
  ): string {
    return value.toLocaleString(
      'de-DE',
      {
        minimumFractionDigits:
          2,

        maximumFractionDigits:
          2,
      }
    );
  }

  formatPercent(
    value:
      number
  ): string {
    return value.toLocaleString(
      'en-US',
      {
        minimumFractionDigits:
          1,

        maximumFractionDigits:
          1,
      }
    );
  }

  private commitMenge(
    rowId:
      string,
    menge:
      number,
    einheit:
      string
  ): string | null {
    const base =
      this.positionen().find(
        (
          current
        ) =>
          current.rowId ===
          rowId
      );

    if (
      !base
    ) {
      return null;
    }

    const option =
      this.findUnitOption(
        base,
        einheit
      );

    if (
      option ===
      null
    ) {
      return null;
    }

    if (
      base.einheit ===
      option.einheit
    ) {
      this.erfassePosition(
        base.rowId,
        menge,
        option.einzelpreis
      );

      this.clearNewArticleHighlight(
        base
      );

      return base.rowId;
    }

    const sibling =
      this.positionen().find(
        (
          current
        ) =>
          current.rowId !==
          base.rowId &&
          current.artikelnummer ===
          base.artikelnummer &&
          current.einheit ===
          option.einheit
      );

    if (
      sibling
    ) {
      this.erfassePosition(
        sibling.rowId,
        menge,
        option.einzelpreis
      );

      this.clearNewArticleHighlight(
        base
      );

      return sibling.rowId;
    }

    const previousNewlyAddedPositionId =
      this.newlyAddedPositionId();

    const neueZeile:
      ReturnPosition = {
      ...base,

      positionId:
        null,

      rowId:
        this.createUnitRowId(
          base.artikelnummer,
          option.einheit
        ),

      einheit:
        option.einheit,

      mengeEinheitId:
        option.id ?? this.returnsService.unitIdFromLabel(option.einheit) ?? base.mengeEinheitId,

      grundEinheitId:
        base.grundEinheitId,

      einzelpreis:
        option.einzelpreis,

      menge,

      gesamtwert:
        this.roundCurrency(
          menge *
          option.einzelpreis
        ),

      status:
        menge === 0
          ? PositionStatus.Gemeldet
          : PositionStatus.Erfasst,

      istNeu:
        false,
    };

    this.pushCreatedUndo(
      neueZeile.rowId,
      previousNewlyAddedPositionId
    );

    this.positionenState.update(
      (
        positionen
      ) => {
        const index =
          positionen.findIndex(
            (
              current
            ) =>
              current.rowId ===
              base.rowId
          );

        const next = [
          ...positionen,
        ];

        next.splice(
          index +
          1,
          0,
          neueZeile
        );

        return next;
      }
    );

    // A new unit is its own Artikel+Einheit row. Persist only that new row.
    this.persistPosition(neueZeile.rowId);

    this.clearNewArticleHighlight(
      base
    );

    return neueZeile.rowId;
  }

  private erfassePosition(
    rowId:
      string,
    menge:
      number,
    einzelpreis:
      number
  ): void {
    if (this.isReadOnly()) {
      return;
    }

    const current =
      this.positionen().find(
        (
          position
        ) =>
          position.rowId ===
          rowId
      );

    if (
      !current
    ) {
      return;
    }

    this.pushUndo(
      current
    );

    this.positionenState.update(
      (
        positionen
      ) =>
        positionen.map(
          (
            position
          ) =>
            position.rowId ===
              rowId
              ? {
                ...position,

                menge,

                einzelpreis,

                gesamtwert:
                  this.roundCurrency(
                    menge *
                    einzelpreis
                  ),

                status:
                  menge === 0 && current.positionId === null
                    ? PositionStatus.Gemeldet
                    : PositionStatus.Erfasst,

                istNeu:
                  false,
              }
              : position
        )
    );

    this.selection.update(
      (
        selected
      ) => {
        if (
          !selected.has(
            rowId
          )
        ) {
          return selected;
        }

        const next =
          new Set(
            selected
          );

        next.delete(
          rowId
        );

        return next;
      }
    );

    this.persistPosition(rowId);
  }

  private pushUndo(
    position:
      ReturnPosition
  ): void {
    const undoEntry:
      UndoUpdateEntry = {
      kind:
        'update',

      positionId:
        position.rowId,

      menge:
        position.menge,

      gesamtwert:
        position.gesamtwert,

      status:
        position.status,

      grund:
        position.grund,

      einheit:
        position.einheit,

      mengeEinheitId:
        position.mengeEinheitId,

      einzelpreis:
        position.einzelpreis,

      istNeu:
        position.istNeu,

      previousNewlyAddedPositionId:
        this.newlyAddedPositionId(),
    };

    this.undoStack.update(
      (
        stack
      ) => [
        ...stack,
        undoEntry,
      ].slice(
        -50
      )
    );
  }

  private pushCreatedUndo(
    positionId:
      string,
    previousNewlyAddedPositionId:
      string | null =
      this.newlyAddedPositionId()
  ): void {
    const undoEntry:
      UndoCreateEntry = {
      kind:
        'create',

      positionId,

      previousNewlyAddedPositionId,
    };

    this.undoStack.update(
      (
        stack
      ) => [
        ...stack,
        undoEntry,
      ].slice(
        -50
      )
    );
  }

  private findUnitOption(
    position:
      ReturnPosition,
    einheit:
      string
  ) {
    return (
      position.verfuegbareEinheiten.find(
        (
          option
        ) =>
          option.einheit ===
          einheit
      ) ??
      null
    );
  }

  private createUnitRowId(
    artikelnummer:
      number,
    einheit:
      string
  ): string {
    const taken =
      new Set(
        this.positionen().map(
          (
            position
          ) =>
            position.rowId
        )
      );

    const base =
      `${artikelnummer}#${einheit}`;

    if (
      !taken.has(
        base
      )
    ) {
      return base;
    }

    let suffix =
      2;

    while (
      taken.has(
        `${base}#${suffix}`
      )
    ) {
      suffix++;
    }

    return `${base}#${suffix}`;
  }

  private clearNewArticleHighlight(
    position:
      ReturnPosition
  ): void {
    const newId =
      this.newlyAddedPositionId();

    if (
      newId ===
      null
    ) {
      return;
    }

    const highlighted =
      this.positionen().find(
        (
          current
        ) =>
          current.rowId ===
          newId
      );

    if (
      highlighted
        ?.artikelnummer ===
      position.artikelnummer
    ) {
      this.newlyAddedPositionId.set(
        null
      );
    }
  }

  private positionExists(
    positionId:
      string | null
  ): boolean {
    if (
      positionId ===
      null
    ) {
      return false;
    }

    return this.positionen().some(
      (
        position
      ) =>
        position.rowId ===
        positionId
    );
  }

  private focusRow(
    id:
      string |
      null,
    initial:
      string,
    einheit?:
      string
  ): void {
    this.activeId.set(
      id
    );

    this.activeUnit.set(
      einheit ??
      DEFAULT_EINHEIT
    );

    this.inputValue.set(
      ''
    );

    this.placeholderValue.set(
      initial
    );
  }

  private flashRow(
    id:
      string
  ): void {
    this.flashId.set(
      id
    );

    setTimeout(
      () => {
        this.flashId.update(
          (
            current
          ) =>
            current ===
              id
              ? null
              : current
        );
      },
      550
    );
  }

  private gotoNextOpen(
    fromId:
      string
  ): void {
    const ids =
      this.displayedPositionen().map(
        (
          position
        ) =>
          position.rowId
      );

    const startIndex =
      ids.indexOf(
        fromId
      );

    for (
      let offset =
        1;
      offset <=
      ids.length;
      offset++
    ) {
      const candidateId =
        ids[
        (
          startIndex +
          offset
        ) %
        ids.length
        ];

      const candidate =
        this.positionen().find(
          (
            position
          ) =>
            position.rowId ===
            candidateId
        );

      if (
        candidate &&
        candidate.rowId !==
        fromId &&
        this.isOffen(
          candidate
        )
      ) {
        this.focusRow(
          candidate.rowId,
          this.mengeRohwert(
            candidate
          ),
          candidate.einheit
        );

        return;
      }
    }

    this.focusRow(
      null,
      ''
    );
  }

  private moveActive(
    fromId:
      string,
    delta:
      number
  ): void {
    const ids =
      this.displayedPositionen().map(
        (
          position
        ) =>
          position.rowId
      );

    const index =
      ids.indexOf(
        fromId
      );

    if (
      index ===
      -1
    ) {
      return;
    }

    const targetIndex =
      index +
      delta;

    if (
      targetIndex <
      0 ||
      targetIndex >=
      ids.length
    ) {
      return;
    }

    const target =
      this.positionen().find(
        (
          position
        ) =>
          position.rowId ===
          ids[
          targetIndex
          ]
      );

    if (
      target
    ) {
      this.focusRow(
        target.rowId,
        this.mengeRohwert(
          target
        ),
        target.einheit
      );
    }
  }

  private currentDraft(): string {
    return (
      this.inputValue()
        .trim() !==
        ''
        ? this.inputValue()
        : this.placeholderValue()
    );
  }

  private currentDraftMenge(): number {
    return (
      this.parseMenge(
        this.currentDraft()
      ) ??
      0
    );
  }

  private parseMenge(
    raw:
      string
  ): number | null {
    const trimmed =
      raw.trim();

    if (
      trimmed ===
      '' ||
      !/^[0-9]+$/.test(
        trimmed
      )
    ) {
      return null;
    }

    const value =
      Number.parseInt(
        trimmed,
        10
      );

    return (
      Number.isNaN(
        value
      ) ||
        value <
        0
        ? null
        : value
    );
  }

  private sanitizeDigits(
    value:
      string
  ): string {
    return value.replace(
      /[^0-9]/g,
      ''
    );
  }

  private roundCurrency(
    value:
      number
  ): number {
    return (
      Math.round(
        value *
        100
      ) /
      100
    );
  }

  private resetEditingState(): void {
    this.activeId.set(
      null
    );

    this.activeUnit.set(
      DEFAULT_EINHEIT
    );

    this.inputValue.set(
      ''
    );

    this.placeholderValue.set(
      ''
    );

    this.flashId.set(
      null
    );

    this.newlyAddedPositionId.set(
      null
    );

    this.undoStack.set(
      []
    );
  }

  private scrollPositionIntoView(rowId: string): void {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const container = this.tableBody()?.nativeElement;

        if (!container) {
          return;
        }

        const row = Array.from(
          container.querySelectorAll<HTMLElement>('[data-return-row-id]')
        ).find((element) => element.dataset['returnRowId'] === rowId);

        row?.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'nearest',
        });
      });
    });
  }

  private persistPosition(rowId: string): void {
    this.persistPositions([rowId]);
  }

  private persistPositions(rowIds: readonly string[]): void {
    if (this.isReadOnly()) return;

    for (const rowId of rowIds) {
      this.pendingSaveRowIds.add(rowId);
    }

    this.flushPendingPositionSaves();
  }

  private flushPendingPositionSaves(): void {
    if (this.saveInFlight || this.pendingSaveRowIds.size === 0 || this.isReadOnly()) {
      return;
    }

    const filialNummer = this.detail()?.filialNummer;
    if (!filialNummer) {
      this.pendingSaveRowIds.clear();
      return;
    }

    const rowIds = [...this.pendingSaveRowIds];
    this.pendingSaveRowIds.clear();

    const entries = rowIds.flatMap((rowId) => {
      const position = this.positionen().find((entry) => entry.rowId === rowId);

      if (!position || position.status !== PositionStatus.Erfasst) {
        return [];
      }

      return [{
        rowId,
        request: {
          id: position.positionId ?? null,
          artikelnummer: position.artikelnummer,
          retoureMenge: position.menge,
          mengeEinheitId: position.mengeEinheitId,
        },
      }];
    });

    if (entries.length === 0) {
      return;
    }

    this.saveInFlight = true;
    this.isSavingPositions.set(true);

    this.returnsService
      .savePositions(
        filialNummer,
        entries.map((entry) => entry.request)
      )
      .subscribe({
        next: (saved) => {
          this.saveInFlight = false;
          this.isSavingPositions.set(false);
          this.applySavedPositionIds(entries, saved);
          this.refreshReturnLock(filialNummer);
          this.flushPendingPositionSaves();
        },
        error: (error: unknown) => {
          this.saveInFlight = false;
          this.isSavingPositions.set(false);
          this.pendingSaveRowIds.clear();

          if (!this.handleMutationLockError(filialNummer, error)) {
            this.loadDetail(filialNummer);
          }
        },
      });
  }

  private applySavedPositionIds(
    entries: readonly {
      rowId: string;
      request: {
        id?: string | null;
        artikelnummer: number;
        retoureMenge: number;
        mengeEinheitId: number;
      };
    }[],
    saved: readonly {
      id: string | null;
      artikelnummer: number;
      retoureMenge: number;
      mengeEinheitId: number;
      geloescht?: boolean;
    }[]
  ): void {
    const resultsByRowId = new Map<
      string,
      { id: string | null; geloescht: boolean }
    >();

    for (const entry of entries) {
      const result = saved.find(
        (item) =>
          item.artikelnummer === entry.request.artikelnummer &&
          item.mengeEinheitId === entry.request.mengeEinheitId
      );

      if (result) {
        resultsByRowId.set(entry.rowId, {
          id: result.id,
          geloescht: result.geloescht === true,
        });
      }
    }

    if (resultsByRowId.size === 0) return;

    this.positionenState.update((positionen) =>
      positionen.map((position) => {
        const result = resultsByRowId.get(position.rowId);

        if (!result) return position;

        if (result.geloescht) {
          return {
            ...position,
            positionId: null,
            menge: 0,
            gesamtwert: 0,
            status: PositionStatus.Gemeldet,
          };
        }

        return result.id
          ? { ...position, positionId: result.id }
          : position;
      })
    );
  }

  private openDetailWithLock(filialNummer: number): void {
    const previousFilialNummer = this.lockedFilialNummer;

    if (
      previousFilialNummer !== null &&
      previousFilialNummer !== filialNummer
    ) {
      this.lockedFilialNummer = null;

      this.returnsService.releaseReturnLock(previousFilialNummer).subscribe({
        next: () => this.acquireLockAndLoadDetail(filialNummer),
        error: () => {
          this.returnsService.clearReturnLock(previousFilialNummer);
          this.acquireLockAndLoadDetail(filialNummer);
        },
      });

      return;
    }

    this.acquireLockAndLoadDetail(filialNummer);
  }

  private acquireLockAndLoadDetail(filialNummer: number): void {
    if (this.returnsService.getReturnLockSession(filialNummer)) {
      this.lockedFilialNummer = filialNummer;
      this.loadDetail(filialNummer);
      return;
    }

    this.isLoading.set(true);

    this.returnsService.acquireReturnLock(filialNummer).subscribe({
      next: () => {
        this.lockedFilialNummer = filialNummer;
        this.loadDetail(filialNummer);
      },
      error: () => {
        this.isLoading.set(false);
        this.detail.set(null);
        this.positionenState.set([]);
        this.selection.set(new Set<string>());
        this.resetEditingState();
        void this.router.navigate(['/retouren']);
      },
    });
  }

  private refreshReturnLock(filialNummer: number): void {
    if (this.lockedFilialNummer !== filialNummer) {
      return;
    }

    this.returnsService.heartbeatReturnLock(filialNummer).subscribe({
      next: (session) => {
        if (session === null) {
          this.handleLostReturnLock(filialNummer);
        }
      },
      error: () => this.handleLostReturnLock(filialNummer),
    });
  }

  private handleMutationLockError(
    filialNummer: number,
    error: unknown
  ): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 423) {
      return false;
    }

    this.handleLostReturnLock(filialNummer);
    return true;
  }

  private handleLostReturnLock(filialNummer: number): void {
    if (this.lockedFilialNummer === filialNummer) {
      this.lockedFilialNummer = null;
    }

    this.returnsService.clearReturnLock(filialNummer);
    void this.router.navigate(['/retouren']);
  }

  private releaseLockAndNavigate(): void {
    const filialNummer = this.lockedFilialNummer;

    if (filialNummer === null) {
      void this.router.navigate(['/retouren']);
      return;
    }

    this.lockedFilialNummer = null;

    this.returnsService.releaseReturnLock(filialNummer).subscribe({
      next: () => void this.router.navigate(['/retouren']),
      error: () => {
        this.returnsService.clearReturnLock(filialNummer);
        void this.router.navigate(['/retouren']);
      },
    });
  }

  private loadLookups(): void {
    this.returnsService
      .getGruende()
      .subscribe({
        next:
          (
            items
          ) => {
            this.gruende.set(
              items.map(
                (
                  item
                ) =>
                  item.bezeichnung
              )
            );
          },

        error:
          () => {
            this.gruende.set(
              []
            );
          },
      });

    this.returnsService
      .getEinheiten()
      .subscribe({
        next:
          (
            items
          ) => {
            this.einheiten.set(
              items.map(
                (
                  item
                ) =>
                  item.bezeichnung
              )
            );
          },

        error:
          () => {
            this.einheiten.set(
              []
            );
          },
      });
  }

  private loadDetail(
    filialNummer:
      number
  ): void {
    this.isLoading.set(
      true
    );

    this.returnsService
      .getReturnDetail(
        filialNummer
      )
      .pipe(
        finalize(
          () =>
            this.isLoading.set(
              false
            )
        )
      )
      .subscribe({
        next:
          (
            detail
          ) => {
            this.detail.set(
              detail
            );

            this.positionenState.set(
              detail.positionen
            );

            this.selection.set(
              new Set<string>()
            );

            this.resetEditingState();
          },

        error:
          () => {
            this.detail.set(
              null
            );

            this.positionenState.set(
              []
            );

            this.selection.set(
              new Set<string>()
            );

            this.resetEditingState();

            if (this.lockedFilialNummer === filialNummer) {
              this.lockedFilialNummer = null;
              this.returnsService.releaseReturnLock(filialNummer).subscribe({
                error: () => this.returnsService.clearReturnLock(filialNummer),
              });
            }
          },
      });
  }
}